import { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Users, Clock, X, Minimize2, Maximize2 } from 'lucide-react';

const JANUS_WSS = 'wss://janus.wicolang.com';

function tx() {
  return Math.random().toString(36).substring(2, 12);
}

function VideoTile({ stream, label, isLocal }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <div className="relative bg-gray-800 rounded-xl overflow-hidden flex items-center justify-center aspect-video">
      {stream ? (
        <video ref={ref} autoPlay playsInline muted={isLocal} className="w-full h-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center text-lg font-bold text-gray-300">
            {label?.[0]?.toUpperCase() || '?'}
          </div>
          <span className="text-xs text-gray-500">카메라 꺼짐</span>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
        {label}{isLocal && ' (나)'}
      </div>
    </div>
  );
}

export default function MeetingPanel({ open, onClose, channel }) {
  const [phase, setPhase] = useState('lobby');
  const [localStream, setLocalStream] = useState(null);
  const [camError, setCamError] = useState('');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteFeeds, setRemoteFeeds] = useState({});
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [minimized, setMinimized] = useState(false);

  const wsRef = useRef(null);
  const sessionRef = useRef(null);
  const pubHandleRef = useRef(null);
  const subHandlesRef = useRef({});
  const pcRef = useRef(null);
  const subPCsRef = useRef({});
  const streamRef = useRef(null);
  const keepRef = useRef(null);
  const timerRef = useRef(null);
  const pendingRef = useRef({});

  const roomId = channel?.id || 1;
  const userName = (() => {
    try {
      const token = localStorage.getItem('paper_work_token');
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      const payload = JSON.parse(json);
      return payload.name || payload.username || '참가자';
    } catch { return '참가자'; }
  })();

  // 패널이 열릴 때만 카메라/마이크 획득
  useEffect(() => {
    if (!open || streamRef.current) return;
    let dead = false;
    setCamError('');
    (async () => {
      let s = null;
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch {
        try {
          s = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          if (!dead) setCamError('카메라를 사용할 수 없습니다. (마이크만 연결됨)');
        } catch {
          if (!dead) setCamError('카메라/마이크 접근이 차단됐습니다. HTTPS 또는 localhost에서 접속하세요.');
        }
      }
      if (dead) { s?.getTracks().forEach(t => t.stop()); return; }
      if (s) { streamRef.current = s; setLocalStream(s); }
    })();
    return () => { dead = true; };
  }, [open]);

  const sendWS = useCallback(msg => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(msg));
  }, []);

  const request = useCallback(msg => new Promise(res => {
    const t = tx();
    pendingRef.current[t] = res;
    sendWS({ ...msg, transaction: t });
    setTimeout(() => { if (pendingRef.current[t]) { delete pendingRef.current[t]; res(null); } }, 10000);
  }), [sendWS]);

  const trickle = useCallback((c, hid) => {
    if (!sessionRef.current || !hid) return;
    sendWS({ janus: 'trickle', session_id: sessionRef.current, handle_id: hid, candidate: c ? { candidate: c.candidate, sdpMid: c.sdpMid, sdpMLineIndex: c.sdpMLineIndex } : { completed: true } });
  }, [sendWS]);

  const subscribe = useCallback(async (feedId) => {
    if (!sessionRef.current) return;
    const r = await request({ janus: 'attach', plugin: 'janus.plugin.videoroom', session_id: sessionRef.current });
    if (!r?.data?.id) return;
    const hid = r.data.id;
    subHandlesRef.current[feedId] = hid;
    sendWS({ janus: 'message', session_id: sessionRef.current, handle_id: hid, transaction: tx(), body: { request: 'join', room: roomId, ptype: 'subscriber', feed: feedId } });
  }, [request, sendWS, roomId]);

  const publishStream = useCallback(async () => {
    const stream = streamRef.current || new MediaStream();
    const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    pcRef.current = pc;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pc.onicecandidate = e => trickle(e.candidate, pubHandleRef.current);
    const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false });
    await pc.setLocalDescription(offer);
    sendWS({ janus: 'message', session_id: sessionRef.current, handle_id: pubHandleRef.current, transaction: tx(), body: { request: 'publish', audio: true, video: true }, jsep: offer });
  }, [trickle, sendWS]);

  const handleMsg = useCallback(async data => {
    const { janus, transaction, sender } = data;
    if (transaction && pendingRef.current[transaction]) { pendingRef.current[transaction](data); delete pendingRef.current[transaction]; }
    if (janus === 'keepalive') return;

    if (sender === pubHandleRef.current && janus === 'event') {
      const body = data.plugindata?.data;
      if (body?.videoroom === 'joined') {
        setStatus('connected');
        // joined 확인 후 publish (올바른 순서)
        publishStream();
        (body.publishers || []).forEach(p => subscribe(p.id, p.display));
      }
      if (body?.videoroom === 'event' && body.publishers) body.publishers.forEach(p => subscribe(p.id, p.display));
      if (body?.videoroom === 'event' && body.unpublished) {
        const fid = body.unpublished;
        setRemoteFeeds(prev => { const n = { ...prev }; delete n[fid]; return n; });
        subPCsRef.current[fid]?.close(); delete subPCsRef.current[fid];
      }
      if (data.jsep) await pcRef.current?.setRemoteDescription(new RTCSessionDescription(data.jsep));
    }

    const sub = Object.entries(subHandlesRef.current).find(([, h]) => h === sender);
    if (sub && janus === 'event' && data.jsep) {
      const feedId = Number(sub[0]);
      const spc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      subPCsRef.current[feedId] = spc;
      spc.onicecandidate = e => trickle(e.candidate, sender);
      spc.ontrack = e => setRemoteFeeds(prev => ({ ...prev, [feedId]: { stream: e.streams[0] || new MediaStream([e.track]), display: data.plugindata?.data?.display || `피드 ${feedId}` } }));
      await spc.setRemoteDescription(new RTCSessionDescription(data.jsep));
      const ans = await spc.createAnswer();
      await spc.setLocalDescription(ans);
      sendWS({ janus: 'message', session_id: sessionRef.current, handle_id: sender, transaction: tx(), body: { request: 'start', room: roomId }, jsep: ans });
    }

    if (janus === 'trickle' && data.candidate && !data.candidate.completed) {
      const sf = Object.entries(subHandlesRef.current).find(([, h]) => h === sender);
      if (sf) subPCsRef.current[Number(sf[0])]?.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
      else if (sender === pubHandleRef.current) pcRef.current?.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
    }
  }, [subscribe, sendWS, trickle, roomId, publishStream]);

  const cleanupJanus = useCallback(() => {
    clearInterval(keepRef.current); clearInterval(timerRef.current);
    pcRef.current?.close();
    Object.values(subPCsRef.current).forEach(p => p.close());
    wsRef.current?.close();
    sessionRef.current = null; pubHandleRef.current = null;
    subHandlesRef.current = {}; subPCsRef.current = {};
    setRemoteFeeds({}); setElapsed(0);
  }, []);

  const join = useCallback(() => {
    setPhase('meeting'); setStatus('connecting'); setErrorMsg('');
    const ws = new WebSocket(JANUS_WSS, 'janus-protocol');
    wsRef.current = ws;
    ws.onmessage = e => { try { handleMsg(JSON.parse(e.data)); } catch {} };
    ws.onerror = () => { setErrorMsg('서버 연결 실패'); setStatus('error'); };
    ws.onopen = async () => {
      const sr = await request({ janus: 'create' });
      if (!sr?.data?.id) { setErrorMsg('세션 생성 실패'); setStatus('error'); return; }
      sessionRef.current = sr.data.id;
      keepRef.current = setInterval(() => sendWS({ janus: 'keepalive', session_id: sessionRef.current, transaction: tx() }), 25000);

      const ar = await request({ janus: 'attach', plugin: 'janus.plugin.videoroom', session_id: sessionRef.current });
      if (!ar?.data?.id) { setErrorMsg('플러그인 연결 실패'); setStatus('error'); return; }
      pubHandleRef.current = ar.data.id;

      // 방이 없으면 자동 생성 (이미 있으면 에러 무시)
      await request({ janus: 'message', session_id: sessionRef.current, handle_id: pubHandleRef.current, body: { request: 'create', room: roomId, publishers: 12, permanent: false, audiolevel_event: false } });

      // join → joined 이벤트 수신 후 handleMsg에서 publish 처리
      sendWS({ janus: 'message', session_id: sessionRef.current, handle_id: pubHandleRef.current, transaction: tx(), body: { request: 'join', room: roomId, ptype: 'publisher', display: userName } });

      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    };
  }, [request, sendWS, handleMsg, trickle, roomId, userName]);

  const leave = useCallback(() => {
    if (sessionRef.current && pubHandleRef.current) sendWS({ janus: 'message', session_id: sessionRef.current, handle_id: pubHandleRef.current, transaction: tx(), body: { request: 'unpublish' } });
    cleanupJanus(); setPhase('lobby'); setStatus('idle');
  }, [sendWS, cleanupJanus]);

  const toggleMic = () => { const n = !micOn; setMicOn(n); streamRef.current?.getAudioTracks().forEach(t => { t.enabled = n; }); };
  const toggleCam = () => { const n = !camOn; setCamOn(n); streamRef.current?.getVideoTracks().forEach(t => { t.enabled = n; }); };

  const lobbyVideoRef = useRef(null);
  useEffect(() => {
    if (lobbyVideoRef.current && localStream) {
      lobbyVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const pipLocalRef = useRef(null);
  useEffect(() => {
    if (minimized && pipLocalRef.current && localStream) pipLocalRef.current.srcObject = localStream;
  }, [minimized, localStream]);

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const cnt = 1 + Object.keys(remoteFeeds).length;
  const grid = cnt <= 1 ? 'grid-cols-1' : cnt <= 2 ? 'grid-cols-2' : cnt <= 4 ? 'grid-cols-2' : 'grid-cols-3';

  // ── PiP 플로팅 위젯 (최소화 상태) ────────────────────────────────────────
  if (!open && !minimized) return null;

  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[70] w-72 rounded-2xl overflow-hidden bg-gray-950 border border-gray-700 shadow-2xl">
        {/* 비디오 */}
        <div className="relative bg-black h-40">
          {localStream ? (
            <video ref={pipLocalRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 font-bold">{userName[0]?.toUpperCase()}</div>
            </div>
          )}
          {/* 상태 배지 */}
          {phase === 'meeting' && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${status === 'connected' ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
              <span className="text-[10px] text-white/80">{fmt(elapsed)}</span>
            </div>
          )}
          {/* 참가자 수 */}
          {phase === 'meeting' && Object.keys(remoteFeeds).length > 0 && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">{cnt}명</div>
          )}
        </div>
        {/* 컨트롤 바 */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-900">
          <div className="flex items-center gap-1.5">
            <button onClick={toggleMic} className={`p-1.5 rounded-full ${micOn ? 'bg-gray-700 text-white' : 'bg-red-600 text-white'}`}>
              {micOn ? <Mic size={12} /> : <MicOff size={12} />}
            </button>
            <button onClick={toggleCam} className={`p-1.5 rounded-full ${camOn ? 'bg-gray-700 text-white' : 'bg-red-600 text-white'}`}>
              {camOn ? <Video size={12} /> : <VideoOff size={12} />}
            </button>
            {phase === 'meeting' && (
              <button onClick={() => { leave(); setMinimized(false); }} className="p-1.5 rounded-full bg-red-600 text-white">
                <PhoneOff size={12} />
              </button>
            )}
          </div>
          <button onClick={() => setMinimized(false)} className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white" title="화면 복귀">
            <Maximize2 size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`fixed inset-0 z-[59] transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />
      <div className={`fixed bottom-0 right-0 z-[60] w-[900px] h-[600px] flex flex-col bg-gray-950 border border-gray-700 rounded-tl-xl overflow-hidden transition-transform duration-300 ${open ? 'translate-y-0' : 'translate-y-full'}`} onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-white font-semibold text-sm">화상회의{channel ? ` — #${channel.name}` : ''}</span>
            {phase === 'meeting' && (
              <>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status === 'connected' ? 'bg-green-500/20 text-green-400' : status === 'connecting' ? 'bg-yellow-500/20 text-yellow-400 animate-pulse' : 'bg-red-500/20 text-red-400'}`}>
                  {status === 'connected' ? '● 연결됨' : status === 'connecting' ? '● 연결 중...' : '● 오류'}
                </span>
                <span className="flex items-center gap-1 text-gray-500 text-xs"><Users className="w-3.5 h-3.5" />{cnt}명</span>
                <span className="flex items-center gap-1 text-gray-500 text-xs"><Clock className="w-3.5 h-3.5" />{fmt(elapsed)}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {phase === 'meeting' && (
              <button onClick={() => { setMinimized(true); }} className="text-gray-500 hover:text-white transition-colors p-1" title="화면 최소화 (PiP)">
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1" title="닫기">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 로비 */}
        {phase === 'lobby' && (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 bg-black flex items-center justify-center p-8">
              <div className="w-full max-w-lg aspect-video bg-gray-900 rounded-2xl overflow-hidden relative">
                {(() => {
                  const hasVideo = localStream?.getVideoTracks().length > 0;
                  return (
                    <>
                      <video ref={lobbyVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ display: hasVideo ? 'block' : 'none' }} />
                      {!hasVideo && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                          <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center text-3xl font-bold text-gray-400">{userName[0]?.toUpperCase()}</div>
                          <span className="text-gray-500 text-sm">{localStream ? '카메라 없음' : '카메라 연결 중...'}</span>
                          {camError && <span className="text-[11px] text-red-400 text-center px-4 leading-tight">{camError}</span>}
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                  <button onClick={toggleMic} className={`p-2.5 rounded-full transition-colors ${micOn ? 'bg-gray-700/80 text-white hover:bg-gray-600' : 'bg-red-600 text-white'}`}>
                    {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                  </button>
                  <button onClick={toggleCam} className={`p-2.5 rounded-full transition-colors ${camOn ? 'bg-gray-700/80 text-white hover:bg-gray-600' : 'bg-red-600 text-white'}`}>
                    {camOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="w-60 flex flex-col bg-gray-900 border-l border-gray-800 p-5 gap-4 flex-shrink-0">
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">회의실 정보</p>
                <div className="bg-gray-800 rounded-xl px-4 py-3 space-y-1">
                  <p className="text-white font-semibold text-sm">{channel?.name || '일반 회의실'}</p>
                  <p className="text-gray-500 text-xs">방 번호 #{roomId}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">참가 이름</p>
                <div className="flex items-center gap-2.5 bg-gray-800 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 rounded-full bg-brand/20 flex items-center justify-center text-brand text-sm font-bold flex-shrink-0">{userName[0]?.toUpperCase()}</div>
                  <span className="text-white text-sm font-medium truncate">{userName}</span>
                </div>
              </div>
              <div className="bg-gray-800/50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-500 leading-relaxed">같은 방 번호를 가진 팀원과 연결됩니다.</p>
              </div>
              <div className="mt-auto">
                <button onClick={join} className="w-full py-3 bg-brand hover:bg-brand/80 text-white rounded-xl text-sm font-semibold transition-colors">
                  지금 참가하기
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 회의 중 */}
        {phase === 'meeting' && (
          <>
            <div className="flex-1 overflow-y-auto p-3 bg-black">
              {errorMsg ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <PhoneOff className="w-10 h-10 text-red-400" />
                  <p className="text-white text-sm">{errorMsg}</p>
                  <button onClick={() => { cleanupJanus(); setPhase('lobby'); setStatus('idle'); setErrorMsg(''); }} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm">로비로 돌아가기</button>
                </div>
              ) : (
                <div className={`grid ${grid} gap-2 h-full`}>
                  <VideoTile stream={localStream} label={userName} isLocal />
                  {Object.entries(remoteFeeds).map(([fid, feed]) => (
                    <VideoTile key={fid} stream={feed.stream} label={feed.display} isLocal={false} />
                  ))}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 bg-gray-900 border-t border-gray-800 px-4 py-3 flex items-center justify-center gap-3">
              <button onClick={toggleMic} className={`p-2.5 rounded-full transition-colors ${micOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </button>
              <button onClick={toggleCam} className={`p-2.5 rounded-full transition-colors ${camOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                {camOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
              </button>
              <div className="w-px h-6 bg-gray-700 mx-1" />
              <button onClick={leave} className="p-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors">
                <PhoneOff className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
